// repo/primary-care-connect/frontend/src/pages/Portal/CommunityNavigator/useNavigatorDialog.ts

import { useLayoutEffect, useRef, type MutableRefObject } from "react";

const COMPACT_NAV_QUERY = "(max-width: 860px)";
const NAVIGATOR_SHELL = ".community-navigator-shell";
const NAVIGATOR_MAIN = "community-navigator-main";
const NAVIGATOR_SIDEBAR = "community-navigator-sidebar";
const NAVIGATOR_SIDEBAR_OPEN = "community-navigator-sidebar--open";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
].join(",");

type ElementRef = {
  readonly current: HTMLElement | null;
};

export type NavigatorDialogOptions = {
  open: boolean;
  onDismiss: () => void;
  dismissDisabled?: boolean;
  dialogRef: ElementRef;
  initialFocusRef: ElementRef;
  returnFocusRef?: ElementRef;
  suppressReturnFocusRef?: MutableRefObject<boolean>;
};

type DialogEntry = {
  id: number;
  order: number;
  dialog: HTMLElement;
  optionsRef: MutableRefObject<NavigatorDialogOptions>;
  returnEl: HTMLElement | null;
  isTop: boolean;
};

type InertRecord = {
  element: HTMLElement;
  previous: boolean;
};

type ScrollLock =
  | {
      mode: "compact";
      scrollY: number;
      htmlOverflow: string;
      bodyOverflow: string;
      bodyPosition: string;
      bodyTop: string;
      bodyLeft: string;
      bodyRight: string;
      bodyWidth: string;
    }
  | {
      mode: "desktop";
      main: HTMLElement;
      mainOverflow: string;
      mainScrollTop: number;
    };

let nextDialogId = 0;
let orderCounter = 0;
const stack: DialogEntry[] = [];
let inertRecords: InertRecord[] = [];
let scrollLock: ScrollLock | null = null;
let listenersBound = false;
let restoringFocus = false;

function readInert(element: HTMLElement): boolean {
  return Boolean((element as HTMLElement & { inert?: boolean }).inert);
}

function writeInert(element: HTMLElement, inert: boolean) {
  (element as HTMLElement & { inert: boolean }).inert = inert;
}

function backdropZIndex(dialog: HTMLElement): number {
  const backdrop = dialog.parentElement;
  const raw = backdrop ? window.getComputedStyle(backdrop).zIndex : "auto";
  const value = Number(raw);
  return Number.isFinite(value) ? value : 0;
}

function getTopDialog(): DialogEntry | null {
  const live = stack.filter((entry) => entry.dialog.isConnected);
  if (!live.length) return null;

  return live.reduce((top, entry) => {
    const topZ = backdropZIndex(top.dialog);
    const entryZ = backdropZIndex(entry.dialog);
    if (entryZ !== topZ) return entryZ > topZ ? entry : top;
    return entry.order > top.order ? entry : top;
  });
}

function isFocusable(element: HTMLElement, dialog: HTMLElement): boolean {
  if (element === dialog) return false;
  if (element.matches(":disabled")) return false;
  if (element.tabIndex < 0) return false;
  if (
    element instanceof HTMLInputElement &&
    element.type === "hidden"
  ) {
    return false;
  }
  if (element.closest("[hidden]")) return false;
  if (element.getAttribute("aria-hidden") === "true") return false;

  const inertHost = element.closest("[inert]");
  if (inertHost && dialog.contains(inertHost)) return false;

  const style = window.getComputedStyle(element);
  if (style.display === "none" || style.visibility === "hidden") return false;

  return true;
}

function getFocusable(dialog: HTMLElement): HTMLElement[] {
  return Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (element) => isFocusable(element, dialog)
  );
}

function focusInitial(entry: DialogEntry) {
  const initial = entry.optionsRef.current.initialFocusRef.current;
  if (
    initial &&
    entry.dialog.contains(initial) &&
    !initial.matches(":disabled")
  ) {
    initial.focus({ preventScroll: true });
    return;
  }

  const focusable = getFocusable(entry.dialog);
  if (focusable[0]) {
    focusable[0].focus({ preventScroll: true });
    return;
  }

  entry.dialog.focus({ preventScroll: true });
}

function canFocus(element: HTMLElement | null): element is HTMLElement {
  if (!element || !element.isConnected) return false;
  if (element.closest("[inert]")) return false;
  if (element.matches(":disabled")) return false;
  return true;
}

function hasFocusableBefore(active: Node, focusable: HTMLElement[]): boolean {
  return focusable.some(
    (element) =>
      (element.compareDocumentPosition(active) &
        Node.DOCUMENT_POSITION_FOLLOWING) ===
      Node.DOCUMENT_POSITION_FOLLOWING
  );
}

function hasFocusableAfter(active: Node, focusable: HTMLElement[]): boolean {
  return focusable.some(
    (element) =>
      (active.compareDocumentPosition(element) &
        Node.DOCUMENT_POSITION_FOLLOWING) ===
      Node.DOCUMENT_POSITION_FOLLOWING
  );
}

function onKeyDown(event: KeyboardEvent) {
  const top = getTopDialog();
  if (!top || !top.dialog.isConnected) return;

  if (event.key === "Escape") {
    event.preventDefault();
    event.stopPropagation();
    if (top.optionsRef.current.dismissDisabled) return;
    top.optionsRef.current.onDismiss();
    return;
  }

  if (event.key !== "Tab") return;

  event.stopPropagation();
  const dialog = top.dialog;
  const focusable = getFocusable(dialog);

  if (!focusable.length) {
    event.preventDefault();
    dialog.focus();
    return;
  }

  const active = document.activeElement;
  const inside = active instanceof Node && dialog.contains(active);
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (!inside || !(active instanceof Node)) {
    event.preventDefault();
    (event.shiftKey ? last : first).focus();
    return;
  }

  if (event.shiftKey) {
    if (active === first || !hasFocusableBefore(active, focusable)) {
      event.preventDefault();
      last.focus();
    }
    return;
  }

  if (active === last || !hasFocusableAfter(active, focusable)) {
    event.preventDefault();
    first.focus();
  }
}

function onFocusIn(event: FocusEvent) {
  if (restoringFocus) return;
  const top = getTopDialog();
  if (!top || !top.dialog.isConnected) return;

  const target = event.target;
  if (target instanceof Node && top.dialog.contains(target)) return;

  restoringFocus = true;
  focusInitial(top);
  restoringFocus = false;
}

function bindListeners() {
  if (listenersBound) return;
  document.addEventListener("keydown", onKeyDown, true);
  document.addEventListener("focusin", onFocusIn, true);
  listenersBound = true;
}

function unbindListeners() {
  if (!listenersBound) return;
  document.removeEventListener("keydown", onKeyDown, true);
  document.removeEventListener("focusin", onFocusIn, true);
  listenersBound = false;
}

function restoreInert() {
  const compact = window.matchMedia(COMPACT_NAV_QUERY).matches;

  inertRecords.forEach((record) => {
    if (record.element.classList.contains(NAVIGATOR_SIDEBAR)) {
      const drawerOpen = record.element.classList.contains(
        NAVIGATOR_SIDEBAR_OPEN
      );
      writeInert(record.element, compact && !drawerOpen);
      return;
    }

    writeInert(record.element, record.previous);
  });

  inertRecords = [];
}

function applyInert(dialog: HTMLElement) {
  restoreInert();

  const shell = dialog.closest(NAVIGATOR_SHELL);
  if (!(shell instanceof HTMLElement)) return;

  let node: HTMLElement | null = dialog;
  while (node && node !== shell) {
    const parent = node.parentElement;
    if (!parent) break;

    for (const child of Array.from(parent.children)) {
      if (!(child instanceof HTMLElement) || child === node) continue;
      if (child.classList.contains(NAVIGATOR_MAIN)) continue;

      inertRecords.push({
        element: child,
        previous: readInert(child),
      });
      writeInert(child, true);
    }

    node = parent;
  }
}

function lockScroll(dialog: HTMLElement) {
  if (scrollLock) return;

  const compact = window.matchMedia(COMPACT_NAV_QUERY).matches;
  if (compact) {
    const scrollY = window.scrollY;
    const html = document.documentElement;
    const body = document.body;
    scrollLock = {
      mode: "compact",
      scrollY,
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    return;
  }

  const main = dialog.closest(`.${NAVIGATOR_MAIN}`);
  if (!(main instanceof HTMLElement)) return;

  const mainScrollTop = main.scrollTop;
  scrollLock = {
    mode: "desktop",
    main,
    mainOverflow: main.style.overflow,
    mainScrollTop,
  };
  main.style.overflow = "hidden";
  main.scrollTop = mainScrollTop;
}

function unlockScroll() {
  if (!scrollLock) return;
  const lock = scrollLock;
  scrollLock = null;

  if (lock.mode === "compact") {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = lock.htmlOverflow;
    body.style.overflow = lock.bodyOverflow;
    body.style.position = lock.bodyPosition;
    body.style.top = lock.bodyTop;
    body.style.left = lock.bodyLeft;
    body.style.right = lock.bodyRight;
    body.style.width = lock.bodyWidth;
    window.scrollTo({ top: lock.scrollY, left: 0, behavior: "auto" });
    return;
  }

  lock.main.style.overflow = lock.mainOverflow;
  lock.main.scrollTop = lock.mainScrollTop;
}

function refreshTopFlags() {
  const top = getTopDialog();
  stack.forEach((entry) => {
    entry.isTop = top?.id === entry.id;
  });
}

function syncBackground() {
  refreshTopFlags();
  const top = stack.find((entry) => entry.isTop && entry.dialog.isConnected) ?? null;

  if (!top) {
    restoreInert();
    unlockScroll();
    unbindListeners();
    return;
  }

  applyInert(top.dialog);
  lockScroll(top.dialog);
  bindListeners();
}

function register(entry: DialogEntry) {
  stack.push(entry);
  syncBackground();

  if (getTopDialog()?.id !== entry.id) return;

  restoringFocus = true;
  focusInitial(entry);
  restoringFocus = false;
}

function unregister(id: number) {
  const index = stack.findIndex((entry) => entry.id === id);
  if (index < 0) return;

  const [entry] = stack.splice(index, 1);
  const wasTop = entry.isTop;
  const suppress =
    entry.optionsRef.current.suppressReturnFocusRef?.current === true;
  if (entry.optionsRef.current.suppressReturnFocusRef) {
    entry.optionsRef.current.suppressReturnFocusRef.current = false;
  }

  syncBackground();

  if (!wasTop || suppress) return;

  restoringFocus = true;
  if (canFocus(entry.returnEl)) {
    entry.returnEl.focus();
  } else {
    const nextTop = getTopDialog();
    if (nextTop) focusInitial(nextTop);
  }
  restoringFocus = false;
}

function captureReturnElement(
  dialog: HTMLElement,
  returnFocusRef: ElementRef | undefined
): HTMLElement | null {
  const explicit = returnFocusRef?.current;
  if (explicit && explicit.isConnected && !dialog.contains(explicit)) {
    return explicit;
  }

  const active = document.activeElement;
  if (
    active instanceof HTMLElement &&
    active.isConnected &&
    active !== document.body &&
    active !== document.documentElement &&
    !dialog.contains(active)
  ) {
    return active;
  }

  return null;
}

export function useNavigatorDialog(options: NavigatorDialogOptions) {
  const idRef = useRef(0);
  if (idRef.current === 0) {
    nextDialogId += 1;
    idRef.current = nextDialogId;
  }

  const optionsRef = useRef(options);
  optionsRef.current = options;

  useLayoutEffect(() => {
    if (!options.open) return undefined;

    const dialog = options.dialogRef.current;
    if (!dialog) return undefined;

    const entry: DialogEntry = {
      id: idRef.current,
      order: (orderCounter += 1),
      dialog,
      optionsRef,
      returnEl: captureReturnElement(dialog, options.returnFocusRef),
      isTop: false,
    };

    register(entry);
    return () => unregister(entry.id);
  }, [options.open, options.dialogRef, options.returnFocusRef]);
}
